import React, { useState, useEffect } from 'react';
import {
  Building,
  Users,
  Briefcase,
  CreditCard,
  Activity,
  Clock,
  Search,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  FileText,
  MessageSquare,
  Download,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabaseClient';

interface ConsultantProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  status: string | null;
  is_2fa_enabled: boolean | null;
  created_at: string;
  is_deleted: boolean | null;
  availability_status: string | null;
  workday_account_status: string | null;
}

interface JobApplication {
  id: string;
  consultant_id: string;
  job_title: string | null;
  company_name: string | null;
  status: string | null;
  created_at: string;
}

interface PipelineTransition {
  id: string;
  consultant_id: string;
  job_posting_id: string | null;
  from_stage: string | null;
  to_stage: string | null;
  transitioned_at: string;
  trigger_source: string | null;
}

interface LoginRecord {
  user_id: string;
  portal: string | null;
  event_type: string | null;
  created_at: string;
}

interface DocumentDownload {
  id: string;
  consultant_id: string;
  downloaded_by: string | null;
  created_at: string;
}

interface TimelineEvent {
  id: string;
  type: 'account_created' | 'job_application' | 'pipeline_transition' | 'login' | 'document_download';
  description: string;
  timestamp: string;
  meta?: string;
}

interface ConsultantDetail {
  profile: ConsultantProfile;
  applications: JobApplication[];
  transitions: PipelineTransition[];
  logins: LoginRecord[];
  downloads: DocumentDownload[];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fullName(profile: ConsultantProfile): string {
  const parts = [profile.first_name, profile.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '(No name)';
}

function formatAppStatus(status: string | null): string {
  if (!status) return 'Unknown';
  if (status.toLowerCase() === 'shortlisted') return 'Pre-Selected';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function StatusBadge({ status }: { status: string | null }) {
  const s = (status || '').toLowerCase();
  let classes = 'bg-gray-100 text-gray-600';
  if (s === 'active') classes = 'bg-emerald-100 text-emerald-700';
  else if (s === 'inactive' || s === 'paused') classes = 'bg-orange-100 text-orange-700';
  else if (s === 'pending') classes = 'bg-yellow-100 text-yellow-700';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${classes}`}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
    </span>
  );
}

function AvailabilityBadge({ availability }: { availability: string | null }) {
  const a = (availability || '').toLowerCase();
  let classes = 'bg-gray-100 text-gray-600';
  if (a === 'available') classes = 'bg-emerald-100 text-emerald-700';
  else if (a === 'unavailable') classes = 'bg-red-100 text-red-700';
  else if (a === 'open_to_opportunities') classes = 'bg-blue-100 text-blue-700';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${classes}`}>
      {availability ? availability.replace(/_/g, ' ') : 'Unknown'}
    </span>
  );
}

function TwoFABadge({ enabled }: { enabled: boolean | null }) {
  if (enabled) {
    return (
      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        2FA On
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
      2FA Off
    </span>
  );
}

function IntegrityBar({ score }: { score: number }) {
  let color = 'bg-red-500';
  if (score >= 75) color = 'bg-emerald-500';
  else if (score >= 50) color = 'bg-yellow-500';
  else if (score >= 25) color = 'bg-orange-500';

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">Integrity Score</span>
        <span className="text-sm font-bold text-gray-900">{score}/100</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className={`h-3 rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

function TimelineIcon({ type }: { type: TimelineEvent['type'] }) {
  const base = 'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0';
  switch (type) {
    case 'account_created':
      return (
        <div className={`${base} bg-emerald-100`}>
          <CheckCircle className="w-4 h-4 text-emerald-600" />
        </div>
      );
    case 'job_application':
      return (
        <div className={`${base} bg-blue-100`}>
          <Briefcase className="w-4 h-4 text-blue-600" />
        </div>
      );
    case 'pipeline_transition':
      return (
        <div className={`${base} bg-yellow-100`}>
          <CreditCard className="w-4 h-4 text-yellow-600" />
        </div>
      );
    case 'login':
      return (
        <div className={`${base} bg-gray-100`}>
          <Activity className="w-4 h-4 text-gray-500" />
        </div>
      );
    case 'document_download':
      return (
        <div className={`${base} bg-teal-100`}>
          <FileText className="w-4 h-4 text-teal-600" />
        </div>
      );
    default:
      return (
        <div className={`${base} bg-gray-100`}>
          <Clock className="w-4 h-4 text-gray-500" />
        </div>
      );
  }
}

export default function ConsultantAudit() {
  const [consultants, setConsultants] = useState<ConsultantProfile[]>([]);
  const [filteredConsultants, setFilteredConsultants] = useState<ConsultantProfile[]>([]);
  const [search, setSearch] = useState('');
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantProfile | null>(null);
  const [detail, setDetail] = useState<ConsultantDetail | null>(null);
  const [loadingConsultants, setLoadingConsultants] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load all consultants on mount
  useEffect(() => {
    const fetchConsultants = async () => {
      setLoadingConsultants(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select(
          'user_id, first_name, last_name, email, status, is_2fa_enabled, created_at, is_deleted, availability_status, workday_account_status'
        )
        .order('created_at', { ascending: false });

      if (!error && data) {
        setConsultants(data as ConsultantProfile[]);
        setFilteredConsultants(data as ConsultantProfile[]);
      }
      setLoadingConsultants(false);
    };
    fetchConsultants();
  }, []);

  // Filter consultants when search changes
  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFilteredConsultants(consultants);
      return;
    }
    setFilteredConsultants(
      consultants.filter((c) => {
        const name = fullName(c).toLowerCase();
        return name.includes(q) || (c.email || '').toLowerCase().includes(q);
      })
    );
  }, [search, consultants]);

  // Load detail when selected consultant changes
  useEffect(() => {
    if (!selectedConsultant) {
      setDetail(null);
      return;
    }
    const fetchDetail = async () => {
      setLoadingDetail(true);
      const userId = selectedConsultant.user_id;

      const [appsRes, transitionsRes, loginsRes, downloadsRes] = await Promise.all([
        supabase
          .from('job_applications')
          .select('id, consultant_id, job_title, company_name, status, created_at')
          .eq('consultant_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('pipeline_stage_transitions')
          .select('id, consultant_id, job_posting_id, from_stage, to_stage, transitioned_at, trigger_source')
          .eq('consultant_id', userId)
          .order('transitioned_at', { ascending: false }),
        supabase
          .from('login_records')
          .select('user_id, portal, event_type, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('consultant_document_downloads')
          .select('id, consultant_id, downloaded_by, created_at')
          .eq('consultant_id', userId)
          .order('created_at', { ascending: false }),
      ]);

      setDetail({
        profile: selectedConsultant,
        applications: appsRes.data || [],
        transitions: transitionsRes.data || [],
        logins: loginsRes.data || [],
        downloads: downloadsRes.data || [],
      });
      setLoadingDetail(false);
    };
    fetchDetail();
  }, [selectedConsultant]);

  function buildTimeline(d: ConsultantDetail): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    events.push({
      id: 'account_created',
      type: 'account_created',
      description: 'Account created',
      timestamp: d.profile.created_at,
    });

    for (const app of d.applications) {
      events.push({
        id: `app_${app.id}`,
        type: 'job_application',
        description: `Applied to: ${app.job_title || 'Unknown role'}${app.company_name ? ` at ${app.company_name}` : ''}`,
        timestamp: app.created_at,
        meta: formatAppStatus(app.status),
      });
    }

    for (const t of d.transitions) {
      events.push({
        id: `trans_${t.id}`,
        type: 'pipeline_transition',
        description: `Pipeline: ${t.from_stage || '?'} → ${t.to_stage || '?'}`,
        timestamp: t.transitioned_at,
        meta: t.trigger_source || undefined,
      });
    }

    for (const login of d.logins) {
      events.push({
        id: `login_${login.created_at}_${Math.random()}`,
        type: 'login',
        description: `Login${login.event_type ? ` (${login.event_type})` : ''}`,
        timestamp: login.created_at,
        meta: login.portal || undefined,
      });
    }

    for (const dl of d.downloads) {
      events.push({
        id: `dl_${dl.id}`,
        type: 'document_download',
        description: 'Document downloaded',
        timestamp: dl.created_at,
      });
    }

    events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return events;
  }

  function calcIntegrity(d: ConsultantDetail): number {
    let score = 0;
    if (d.profile.is_2fa_enabled) score += 25;
    if ((d.profile.status || '').toLowerCase() === 'active') score += 25;
    if (d.profile.email && d.profile.first_name) score += 25;
    if ((d.profile.availability_status || '').toLowerCase() === 'available') score += 25;
    return score;
  }

  const preSelectedCount = detail
    ? detail.applications.filter((a) => (a.status || '').toLowerCase() === 'shortlisted').length
    : 0;

  const hiredCount = detail
    ? detail.applications.filter((a) => (a.status || '').toLowerCase() === 'hired').length
    : 0;

  return (
    <AdminLayout>
      <div className="flex flex-col h-full min-h-screen bg-gray-50">
        {/* Page header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-white flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Consultant Audit</h1>
            <p className="text-sm text-gray-500 mt-1">Per-consultant audit timelines and integrity scores</p>
          </div>
          {detail && (
            <button
              onClick={() => {
                const events = buildTimeline(detail);
                const rows = events.map((e) => [e.type, e.description, e.timestamp, JSON.stringify(e.meta ?? {})]);
                const csv = [['type', 'description', 'timestamp', 'meta'], ...rows]
                  .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
                  .join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `consultant-audit-${selectedConsultant?.user_id ?? 'export'}-${Date.now()}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left panel */}
          <div className="w-1/3 border-r border-gray-200 bg-white flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
            </div>

            {/* Consultant list */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {loadingConsultants ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                </div>
              ) : filteredConsultants.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">No consultants found</div>
              ) : (
                filteredConsultants.map((consultant) => {
                  const isSelected = selectedConsultant?.user_id === consultant.user_id;
                  return (
                    <button
                      key={consultant.user_id}
                      onClick={() => setSelectedConsultant(consultant)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                        isSelected ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-semibold text-gray-600">
                        {consultant.first_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {fullName(consultant)}
                          </span>
                          <StatusBadge status={consultant.status} />
                          <AvailabilityBadge availability={consultant.availability_status} />
                          <TwoFABadge enabled={consultant.is_2fa_enabled} />
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{consultant.email}</p>
                      </div>
                      {isSelected && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {!selectedConsultant ? (
              <div className="flex flex-col items-center justify-center h-full py-32 text-center">
                <Users className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 text-base font-medium">Select a consultant to view audit timeline</p>
                <p className="text-gray-400 text-sm mt-1">Choose a consultant from the left panel</p>
              </div>
            ) : loadingDetail ? (
              <div className="flex items-center justify-center py-32">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              </div>
            ) : detail ? (
              <div className="p-6 space-y-6">
                {/* Consultant header */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 text-lg font-bold text-gray-600">
                      {detail.profile.first_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-bold text-gray-900">{fullName(detail.profile)}</h2>
                        <StatusBadge status={detail.profile.status} />
                        <AvailabilityBadge availability={detail.profile.availability_status} />
                        {!detail.profile.is_2fa_enabled && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            2FA Not Enabled
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{detail.profile.email}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          Member since {new Date(detail.profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          {detail.profile.is_2fa_enabled ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-orange-400" />
                          )}
                          2FA {detail.profile.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        {detail.profile.workday_account_status && (
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4 text-gray-400" />
                            Workday: {detail.profile.workday_account_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <Briefcase className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-900">{detail.applications.length}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Total Applications</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <CheckCircle className="w-5 h-5 text-teal-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-900">{preSelectedCount}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Pre-Selected</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <Users className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-900">{hiredCount}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Hired</p>
                  </div>
                </div>

                {/* Integrity score */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <IntegrityBar score={calcIntegrity(detail)} />
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
                    <span className={`flex items-center gap-1 ${detail.profile.is_2fa_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> 2FA enabled (+25)
                    </span>
                    <span className={`flex items-center gap-1 ${(detail.profile.status || '').toLowerCase() === 'active' ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Account active (+25)
                    </span>
                    <span className={`flex items-center gap-1 ${(detail.profile.email && detail.profile.first_name) ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Profile complete (+25)
                    </span>
                    <span className={`flex items-center gap-1 ${(detail.profile.availability_status || '').toLowerCase() === 'available' ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Available (+25)
                    </span>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-gray-500" />
                    Audit Timeline
                  </h3>
                  {buildTimeline(detail).length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No events recorded</p>
                  ) : (
                    <ol className="relative border-l border-gray-200 ml-4 space-y-6">
                      {buildTimeline(detail).map((event) => (
                        <li key={event.id} className="ml-4">
                          <div className="absolute -left-4">
                            <TimelineIcon type={event.type} />
                          </div>
                          <div className="pl-2">
                            <p className="text-sm font-medium text-gray-900">{event.description}</p>
                            {event.meta && (
                              <p className="text-xs text-gray-500 mt-0.5">{event.meta}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">{formatDate(event.timestamp)}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
